import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { graphql, navigate } from 'gatsby';
import {
  Button,
  Close,
  Modal,
  ModalBody,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from 'chaoskit/src/components';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import BaseLayout from '../layouts/BaseLayout';
import { Data, Icon } from '../components';
import { config } from '../helpers/config';
import Hero from '../partials/Hero';
import ProductOverview from '../partials/product/ProductOverview';
import ProductTest from '../partials/product/ProductTest';
import ProductScenarios from '../partials/product/ProductScenarios';
import ProductKubernetes from '../partials/product/ProductKubernetes';

const Product = ({
  data: {
    file: { childDataYaml: pageData },
  },
  location,
}) => {
  const heroRef = useRef();
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const setInitialActiveIndex = hash => {
    switch (hash) {
      case '#test':
        return 1;
      case '#scenarios':
        return 2;
      case '#kubernetes':
        return 3;
      default:
        return 0;
    }
  };

  // Force scroll to top on load unless we're about to scroll to a hash onload
  useEffect(() => {
    if (location.hash) {
      setActiveIndex(setInitialActiveIndex(location.hash));
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  useUpdateEffect(() => {
    window.scrollTo({
      top: heroRef.current.scrollHeight,
    });
  }, [activeIndex]);

  const handleTabChange = index => {
    let hash;

    switch (index) {
      case 1:
        hash = 'test';
        break;
      case 2:
        hash = 'scenarios';
        break;
      case 3:
        hash = 'kubernetes';
        break;
      default:
        hash = null;
    }

    navigate(hash ? `${location.pathname}#${hash}` : location.pathname, {
      replace: true,
    });

    setActiveIndex(index);
  };

  return (
    <BaseLayout headerProps={{ sticky: false }} footerProps={{ hideCTA: true }}>
      <Helmet
        title={pageData.title}
        meta={[{ name: 'description', content: pageData.description }]}
      />
      <Hero
        ref={heroRef}
        particles={false}
        className={cx('product__hero', {
          'product__hero--test': activeIndex === 1,
          'product__hero--scenarios': activeIndex === 2,
          'product__hero--k8s': activeIndex === 3,
        })}
      >
        <div className="hero-content u-textCenter">
          <div className="container">
            <h1 className="hero-title hero-title--large">
              <Data>{pageData.hero.heading}</Data>
            </h1>
            <div className="hero-titleSub">
              <Data markdown>{pageData.hero.blurb}</Data>
            </div>
            <Button
              noContrast
              type="primary"
              className="u-mt--large"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'Click', {
                    event_category: 'Demo Video',
                    event_label: 'Product hero',
                  });
                }

                setDemoModalOpen(true);
              }}
            >
              <Data>{pageData.hero.cta.label}</Data>
            </Button>
            <Modal
              open={demoModalOpen}
              onOutsideModalClick={() => setDemoModalOpen(false)}
              className="shared__videoModal"
              size="large"
            >
              <ModalBody>
                <Close
                  className="u-textContrast"
                  onClick={() => setDemoModalOpen(false)}
                />
                <div className="shared__responsiveVideo">
                  <iframe
                    title="Gremlin Demo"
                    width="640"
                    height="360"
                    src={config.demoVideoEmbed}
                    frameBorder="0"
                    allow="autoplay"
                  />
                </div>
              </ModalBody>
            </Modal>
          </div>
        </div>
      </Hero>
      <Tabs
        selectedIndex={activeIndex}
        onSelect={handleTabChange}
        className="product__tabs"
      >
        <TabList className="product__tabList">
          <Tab className="product__tab">
            <span className="u-hiddenDown@small">
              Chaos Engineering Platform
            </span>
            <span className="u-hiddenUp@medium">
              <Icon size="large" icon="docs__fallback" />
            </span>
          </Tab>
          <Tab className="product__tab">
            <span className="u-hiddenDown@small">
              Test
              <br />
              Everything
            </span>
            <span className="u-hiddenUp@medium">
              <Icon size="large" icon="check-square" />
            </span>
          </Tab>
          <Tab className="product__tab">
            <span className="u-hiddenDown@small">
              Real World
              <br /> Scenarios
            </span>
            <span className="u-hiddenUp@medium">
              <Icon size="large" icon="docs__scenarios" />
            </span>
          </Tab>
          <Tab className="product__tab">
            <span className="u-hiddenDown@small">
              Chaos Engineering on Kubernetes
            </span>
            <span className="u-hiddenUp@medium">
              <Icon size="large" icon="kubernetes" />
            </span>
          </Tab>
        </TabList>
        <div className="container">
          <TabPanel className="products__tabPanel">
            <ProductOverview />
          </TabPanel>
          <TabPanel className="products__tabPanel">
            <ProductTest />
          </TabPanel>
          <TabPanel className="products__tabPanel">
            <ProductScenarios />
          </TabPanel>
          <TabPanel className="products__tabPanel">
            <ProductKubernetes />
          </TabPanel>
        </div>
      </Tabs>
    </BaseLayout>
  );
};

Product.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export const pageQuery = graphql`
  query ProductPageData {
    file(name: { eq: "product" }) {
      childDataYaml {
        title
        description
        hero {
          blurb
          cta {
            label
          }
          heading
        }
      }
    }
  }
`;

export default Product;
